
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { supabase } from "@/integrations/supabase/client";
import { databaseMonitor } from "@/utils/performance/databaseMonitoring";
import { useIsAdmin } from "@/hooks/useIsAdmin";
import { RefreshCw, Database, AlertTriangle, CheckCircle, XCircle } from "lucide-react";

interface DebugInfo {
  userInfo: {
    id: string;
    email: string;
    authenticated: boolean;
  } | null;
  credentialStats: {
    totalCredentials: number;
    activeCredentials: number;
    platformBreakdown: Record<string, number>;
  };
  recentErrors: Array<{
    timestamp: Date;
    error: string;
    context?: string;
  }>;
  databaseHealth: {
    connectionStatus: 'healthy' | 'warning' | 'error';
    recentErrors: number;
  };
}

export const CredentialDebugPanel = () => {
  const { data: isAdmin, isLoading: isLoadingAdmin } = useIsAdmin();
  const [debugInfo, setDebugInfo] = useState<DebugInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const loadDebugInfo = async () => {
    if (!isAdmin) return;
    
    try {
      setIsLoading(true);
      
      // Get user info
      const { data: { user } } = await supabase.auth.getUser();
      
      // Get credential stats
      const { data: credentials, error: credError } = await supabase
        .from('platform_credentials')
        .select('platform, is_active, user_id')
        .eq('user_id', user?.id || '');

      if (credError) {
        console.error('Error loading credential stats:', credError);
      }

      // Get platform breakdown
      const platformBreakdown: Record<string, number> = {};
      credentials?.forEach(cred => {
        platformBreakdown[cred.platform] = (platformBreakdown[cred.platform] || 0) + 1;
      });

      // Get database monitoring info
      const recentErrors = databaseMonitor.getRecentErrors(5);
      const errorStats = databaseMonitor.getErrorStats();

      setDebugInfo({
        userInfo: user ? {
          id: user.id,
          email: user.email || 'Unknown',
          authenticated: true
        } : null,
        credentialStats: {
          totalCredentials: credentials?.length || 0,
          activeCredentials: credentials?.filter(c => c.is_active).length || 0,
          platformBreakdown
        },
        recentErrors: recentErrors.map(err => ({
          timestamp: err.timestamp,
          error: err.error,
          context: err.context
        })),
        databaseHealth: {
          connectionStatus: errorStats.last5Minutes > 5 ? 'error' : 
                          errorStats.last5Minutes > 2 ? 'warning' : 'healthy',
          recentErrors: errorStats.last5Minutes
        }
      });

    } catch (error) {
      console.error('Error loading debug info:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isAdmin && !isLoadingAdmin) {
      loadDebugInfo();
    }
  }, [isAdmin, isLoadingAdmin]);

  if (isLoadingAdmin) {
    return null;
  }

  if (!isAdmin) {
    return null;
  }

  return (
    <Card className="mt-6 border-orange-200 bg-orange-50">
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-orange-800">
          <Database className="h-5 w-5" />
          Credential Debug Panel
          <Badge variant="outline">Admin Only</Badge>
        </CardTitle>
        <CardDescription>
          Diagnostic information for troubleshooting credential issues
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex justify-between items-center">
          <h4 className="font-medium">Debug Information</h4>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={loadDebugInfo}
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 mr-1 ${isLoading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {debugInfo && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* User Info */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">User Information</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                {debugInfo.userInfo ? (
                  <>
                    <div className="flex items-center gap-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Authenticated</span>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      ID: {debugInfo.userInfo.id}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Email: {debugInfo.userInfo.email}
                    </div>
                  </>
                ) : (
                  <div className="flex items-center gap-2">
                    <XCircle className="h-4 w-4 text-red-600" />
                    <span className="text-sm">Not authenticated</span>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Credential Stats */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Credential Statistics</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span>Total Credentials:</span>
                  <Badge variant="outline">{debugInfo.credentialStats.totalCredentials}</Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Active Credentials:</span>
                  <Badge variant="outline">{debugInfo.credentialStats.activeCredentials}</Badge>
                </div>
                {Object.entries(debugInfo.credentialStats.platformBreakdown).length > 0 && (
                  <div className="pt-2 border-t">
                    <div className="text-xs text-muted-foreground mb-1">By Platform:</div>
                    {Object.entries(debugInfo.credentialStats.platformBreakdown).map(([platform, count]) => (
                      <div key={platform} className="flex justify-between text-xs">
                        <span>{platform}:</span>
                        <span>{count}</span>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Database Health */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Database Health</CardTitle>
              </CardHeader>
              <CardContent className="space-y-2">
                <div className="flex justify-between items-center text-sm">
                  <span>Connection Status:</span>
                  <Badge 
                    variant={debugInfo.databaseHealth.connectionStatus === 'healthy' ? 'default' : 'destructive'}
                  >
                    {debugInfo.databaseHealth.connectionStatus}
                  </Badge>
                </div>
                <div className="flex justify-between text-sm">
                  <span>Recent Errors (5min):</span>
                  <Badge variant="outline">{debugInfo.databaseHealth.recentErrors}</Badge>
                </div>
              </CardContent>
            </Card>

            {/* Recent Errors */}
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm">Recent Errors</CardTitle>
              </CardHeader>
              <CardContent>
                {debugInfo.recentErrors.length > 0 ? (
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {debugInfo.recentErrors.slice(0, 3).map((error, index) => (
                      <Alert key={index} className="p-2">
                        <AlertTriangle className="h-3 w-3" />
                        <AlertDescription className="text-xs">
                          <div className="font-medium">{error.error}</div>
                          {error.context && (
                            <div className="text-muted-foreground mt-1">{error.context}</div>
                          )}
                          <div className="text-muted-foreground mt-1">
                            {error.timestamp.toLocaleTimeString()}
                          </div>
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground">No recent errors</div>
                )}
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
